import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, X, Gift } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { fetchWorkVideos } from '../services/youtube';
import type { YouTubeVideo } from '../services/youtube';
import ToolsCarousel from '../components/ToolsCarousel';
import { useIsMobile } from '../hooks/use-mobile';

// Auto-play on scroll reel card using direct Cloudinary MP4
function AutoPlayReelCard({ src, index, isMuted, onUnmute }: { src: string; index: number; isMuted: boolean; onUnmute: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // sync muted state
  useEffect(() => {
    const el = videoRef.current;
    if (el) el.muted = isMuted;
  }, [isMuted]);

  return (
    <motion.div
      className="group relative overflow-hidden bg-black"
      style={{ aspectRatio: '9/16' }}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, delay: index * 0.15, ease: 'easeInOut' }}
    >
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

      {/* Mute / unmute button */}
      <button
        onClick={onUnmute}
        className="absolute bottom-3 right-3 w-8 h-8 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110 z-10"
        style={{
          background: 'rgba(0,0,0,0.55)',
          border: `1px solid ${isMuted ? 'rgba(255,255,255,0.2)' : '#0be3c5'}`,
          color: isMuted ? 'rgba(255,255,255,0.6)' : '#0be3c5',
          boxShadow: isMuted ? 'none' : '0 0 10px rgba(11,227,197,0.4)',
          backdropFilter: 'blur(6px)',
        }}
      >
        {isMuted ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
          </svg>
        )}
      </button>
    </motion.div>
  );
}

const HERO_VIDEO_1 = 'https://res.cloudinary.com/ddsnj941k/video/upload/Mix_Of_Best_t3wnqv.mp4';
const HERO_VIDEO = 'https://res.cloudinary.com/ddsnj941k/video/upload/hero_yiyrov.mp4';

const REELS = [
  { id: 'Task_01',      src: 'https://res.cloudinary.com/ddsnj941k/video/upload/Task_01_hsp49p.mp4' },
  { id: 'Chemistry',    src: 'https://res.cloudinary.com/ddsnj941k/video/upload/Chemistry_Class_Last_01_rptbdk.mp4' },
  { id: 'Sashi',        src: 'https://res.cloudinary.com/ddsnj941k/video/upload/Sashi_Investments_-_V5_wdytme.mp4' },
  { id: '01_gfmbe1',   src: 'https://res.cloudinary.com/ddsnj941k/video/upload/01_gfmbe1.mp4' },
  { id: 'next_one',    src: 'https://res.cloudinary.com/ddsnj941k/video/upload/next_one_nuaclu.mp4' },
];

function ReelsSlider() {
  const [offset, setOffset] = useState(0);
  const [unmutedId, setUnmutedId] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const visible = isMobile ? 2 : 4;
  const max = REELS.length - visible;

  const prev = () => setOffset(o => Math.max(0, o - 1));
  const next = () => setOffset(o => Math.min(max, o + 1));

  return (
    <div className="px-6 md:px-10 relative">
      <div className="overflow-hidden">
        <motion.div
          className="flex gap-3"
          animate={{ x: `calc(-${offset} * (100% / ${visible} + 3px))` }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        >
          {REELS.map((reel, index) => (
            <div
              key={reel.id}
              className="flex-shrink-0"
              style={{ width: `calc(${100 / visible}% - ${(visible - 1) * 3 / visible}px)` }}
            >
              <AutoPlayReelCard
                src={reel.src}
                index={index}
                isMuted={unmutedId !== reel.id}
                onUnmute={() => setUnmutedId(prev => prev === reel.id ? null : reel.id)}
              />
            </div>
          ))}
        </motion.div>
      </div>

      {/* Dots + arrows */}
      <div className="flex items-center justify-center gap-3 mt-5">
        <button
          onClick={prev}
          disabled={offset === 0}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
          style={{ background: 'rgba(255,255,255,0.06)', color: offset === 0 ? 'rgba(255,255,255,0.2)' : 'white', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>

        {Array.from({ length: max + 1 }).map((_, i) => (
          <button
            key={i}
            onClick={() => setOffset(i)}
            style={{
              width: offset === i ? '24px' : '8px',
              height: '8px',
              borderRadius: '999px',
              background: offset === i ? '#0be3c5' : 'rgba(255,255,255,0.25)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.4s ease',
              padding: 0,
            }}
          />
        ))}

        <button
          onClick={next}
          disabled={offset === max}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300"
          style={{ background: 'rgba(255,255,255,0.06)', color: offset === max ? 'rgba(255,255,255,0.2)' : 'white', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  );
}

export default function Home() {
  const isMobile = useIsMobile();
  const [featuredVideos, setFeaturedVideos] = useState<YouTubeVideo[]>([]);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [heroVideoIndex, setHeroVideoIndex] = useState(0); // 0 = first video, 1 = hero loop
  const [heroMuted, setHeroMuted] = useState(true);
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowPopup(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const toggleHeroMute = () => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setHeroMuted(v.muted);
  };

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const workVideos = await fetchWorkVideos();
        setFeaturedVideos(workVideos.slice(0, 4));
      } catch (error) {
        console.error('Error loading videos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadVideos();
  }, []);

  const handleVideoClick = (videoId: string) => {
    if (playingVideo === videoId) {
      setPlayingVideo(null);
    } else {
      setPlayingVideo(videoId);
    }
  };

  const clientReviews = [
    {
      id: '3',
      name: 'Emma R.',
      projectType: 'Brand Film',
      feedback: 'Creative vision and technical excellence. Will work with again.',
    },
    {
      id: '2',
      name: 'John D.',
      projectType: 'Commercial',
      feedback: 'Professional work with fast turnaround. Exceeded expectations.',
    },
    {
      id: '1',
      name: 'Sarah M.',
      projectType: 'YouTube Editing',
      feedback: 'CAYLO delivered an incredible cinematic edit. Highly recommended.',
    },
  ];



  return (
    <div>
      {/* â”€â”€ Preset Coming Soon Popup â”€â”€ */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            className="fixed inset-0 z-[999] flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowPopup(false)}
            />

            {/* Card */}
            <motion.div
              className="relative w-full max-w-sm rounded-2xl border border-white/10 p-7 text-white"
              style={{ background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(20px)', boxShadow: '0 0 60px rgba(11,227,197,0.15)' }}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              {/* Close button */}
              <button
                onClick={() => setShowPopup(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-white/40 hover:text-white transition-colors"
                style={{ background: 'rgba(255,255,255,0.06)' }}
              >
                <X size={15} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(11,227,197,0.12)', border: '2px solid rgba(11,227,197,0.4)' }}>
                  <Gift size={22} style={{ color: '#0be3c5' }} />
                </div>
              </div>

              {/* Text */}
              <div className="text-center mb-5">
                <span className="text-xs tracking-[0.35em] uppercase mb-2 block" style={{ color: '#0be3c5' }}>
                  🎉 10K ACHIEVED
                </span>
                <h2 className="text-2xl font-black mb-1">Claim Your Gift</h2>
                <p className="text-white/50 text-sm">All presets now permanently unlocked!</p>
              </div>

              {/* Follower count */}
              <div className="rounded-xl p-4 mb-5 text-center" style={{ background: 'rgba(11, 227, 197, 0.08)', border: '1px solid rgba(11, 227, 197, 0.3)' }}>
                <div className="mb-1">
                  <span className="text-4xl font-black" style={{ color: '#0be3c5' }}>10,500</span>
                  <span className="text-white/30 text-base ml-2">/ 10K</span>
                </div>
                <p className="text-white/40 text-xs mb-3">TikTok followers</p>
                {/* Progress bar */}
                <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: 'linear-gradient(90deg, #0be3c5, #0891b2)' }}
                    initial={{ width: 0 }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 1, delay: 0.5, ease: 'easeOut' }}
                  />
                </div>
                <p className="text-white/60 text-xs mt-2 font-medium">🎉 TARGET ACHIEVED! 🎉</p>
              </div>

              {/* CTA */}
              <Link
                to="/preset"
                onClick={() => setShowPopup(false)}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-black text-sm transition-all duration-300 hover:opacity-90"
                style={{ background: '#0be3c5' }}
              >
                <Gift size={16} />
                Claim Free Presets
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section
        className="relative h-screen w-full overflow-hidden"
        style={{ clipPath: 'ellipse(120% 92% at 50% 0%)' }}
      >
        {/* Background Video â€” desktop only */}
        <div className="absolute inset-0 z-0">
          {!isMobile && (
            <>
              {/* Video 1: plays first, then switches to hero loop */}
              <video
                ref={heroVideoRef}
                key={heroVideoIndex}
                autoPlay
                muted
                playsInline
                preload="auto"
                loop={heroVideoIndex === 1}
                onEnded={() => setHeroVideoIndex(1)}
                className="absolute inset-0 w-full h-full"
                style={{ objectFit: 'cover', objectPosition: 'center' }}
              >
                <source src={heroVideoIndex === 0 ? HERO_VIDEO_1 : HERO_VIDEO} type="video/mp4" />
              </video>

              {/* Mute / Unmute button â€” moved to hero content for visibility */}
            </>
          )}

          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <motion.span
            className="text-sm tracking-[0.4em] uppercase mb-4"
            style={{ color: '#0be3c5' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            CAYLO STUDIO
          </motion.span>

          <motion.h1
            className="text-[10vw] md:text-[7vw] lg:text-[6vw] font-black text-white leading-tight select-none text-center"
            initial={{ opacity: 0, y: 98 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: 'easeInOut' }}
            style={{ textShadow: '0 0 100px rgba(0,0,0,0.8)' }}
          >
            Where Vision<br />Becomes Visual
          </motion.h1>

          <motion.p
            className="text-white/80 text-lg md:text-xl text-center max-w-3xl mb-10 mt-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
          >
            Cinematic edits, social content, and storytelling designed to stand out in a crowded world.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <Link
              to="/work"
              className="btn-primary inline-flex items-center justify-center"
            >
              View My Work
            </Link>
            <Link to="/reels" className="btn-outline inline-flex items-center justify-center">
              View Reels
            </Link>
          </motion.div>

          {/* Social Icons Bar */}
          <motion.div
            className="flex items-center gap-1 mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.5 }}
          >
            {[
              {
                label: 'TikTok',
                href: 'https://www.tiktok.com/@cayloedit?is_from_webapp=1&sender_device=pc',
                icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>,
              },
              {
                label: 'Instagram',
                href: 'https://www.instagram.com/caylostudio?igsh=d2t0eDNodGs3bTN6',
                icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
              },
              {
                label: 'Facebook',
                href: 'https://www.facebook.com/share/1BKagu7dEZ/',
                icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
              },
              {
                label: 'YouTube',
                href: 'https://www.youtube.com/@Obxfilm',
                icon: <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
              },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-10 h-10 flex items-center justify-center rounded-full text-white/50 hover:text-white transition-all duration-300 hover:scale-110"
                style={{ color: 'rgba(255,255,255,0.5)' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#0be3c5')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
              >
                {s.icon}
              </a>
            ))}
          </motion.div>

          {/* Dots + mute â€” under social icons */}
          {!isMobile && (
            <motion.div
              className="flex items-center gap-3 mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.7 }}
            >
              {/* 2 dots */}
              {[0, 1].map(i => (
                <button
                  key={i}
                  onClick={() => setHeroVideoIndex(i)}
                  style={{
                    width: heroVideoIndex === i ? '28px' : '8px',
                    height: '8px',
                    borderRadius: '999px',
                    background: heroVideoIndex === i ? '#0be3c5' : 'rgba(255,255,255,0.25)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.4s ease',
                    padding: 0,
                  }}
                />
              ))}

              {/* Round mute button */}
              <button
                onClick={toggleHeroMute}
                title={heroMuted ? 'Unmute' : 'Mute'}
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: `1px solid ${heroMuted ? 'rgba(255,255,255,0.15)' : '#0be3c5'}`,
                  color: heroMuted ? 'rgba(255,255,255,0.45)' : '#0be3c5',
                  boxShadow: heroMuted ? 'none' : '0 0 12px rgba(11,227,197,0.35)',
                }}
              >
                {heroMuted ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <line x1="23" y1="9" x2="17" y2="15"/>
                    <line x1="17" y1="9" x2="23" y2="15"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                  </svg>
                )}
              </button>
            </motion.div>
          )}

        </div>

      </section>

      {/* Professional Tools Section */}
      <section className="pt-2 pb-10 md:pt-4 md:pb-12 bg-black relative z-10 -mt-10 md:-mt-16">
        <div className="w-full">
          <ToolsCarousel />
        </div>
      </section>



      {/* Reels Preview Section */}
      <section className="pb-10 bg-black" style={{ paddingTop: '40px' }}>
        <div className="px-6 md:px-10">
          <motion.div
            className="flex items-end justify-between mb-2 md:mb-4"
            initial={{ opacity: 0, y: 98 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: 'easeInOut' }}
          >
            <div>
              <span className="text-white/40 text-sm tracking-[0.3em] uppercase">Social Media</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-2">Reels</h2>
            </div>
          </motion.div>
        </div>

        <ReelsSlider />

        <div className="mt-10 text-center">
          <Link
            to="/reels"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-lg"
          >
            View All Reels <ArrowRight size={20} />
          </Link>
        </div>
      </section>



      {/* Featured Work Section */}
      <section className="pt-8 pb-10 md:pt-8 bg-black">
        <div className="px-6 md:px-10">
          <motion.div
            className="flex items-end justify-between mb-2 md:mb-4"
            initial={{ opacity: 0, y: 98 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: 'easeInOut' }}
          >
            <div>
              <span className="text-white/40 text-sm tracking-[0.3em] uppercase">Portfolio</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-2">Featured Work</h2>
            </div>
          </motion.div>

          {/* Videos Grid */}
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-video bg-white/5 rounded-sm animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {featuredVideos.map((video, index) => (
                <motion.div
                  key={video.id}
                  className="group relative overflow-hidden bg-black cursor-pointer"
                  initial={{ opacity: 0, y: 98 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: index * 0.3, ease: 'easeInOut' }}
                  onClick={() => handleVideoClick(video.videoId)}
                >
                  {playingVideo === video.videoId ? (
                    <div className="relative aspect-video">
                      <iframe
                        src={`${video.embedUrl}&autoplay=1`}
                        title={video.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                      />
                      <button
                        onClick={e => { e.stopPropagation(); setPlayingVideo(null); }}
                        className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-lg backdrop-blur-sm transition-colors z-10"
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="text-white">
                          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="relative aspect-video">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ background: 'rgba(11,227,197,0.2)', border: '2px solid #0be3c5', boxShadow: '0 0 24px rgba(11,227,197,0.4)' }}>
                          <Play size={18} className="ml-0.5" style={{ color: '#0be3c5' }} fill="#0be3c5" />
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h3 className="text-white font-bold uppercase text-xs md:text-sm leading-tight"
                          style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                          {video.title}
                        </h3>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-10 text-center">
            <Link
              to="/work"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-lg"
            >
              See More Projects <ArrowRight size={20} />
            </Link>
          </div>


        </div>
      </section>



      {/* Hyper Edit Section â€” MOVED UP */}
      <section className="relative w-full bg-black overflow-hidden" style={{ minHeight: '80vh' }}>
        {!isMobile && (
          <video autoPlay muted loop playsInline preload="none" className="absolute inset-0 w-full h-full object-cover z-0" style={{ filter: 'brightness(0.85)' }}>
            <source src="https://res.cloudinary.com/ddsnj941k/video/upload/FB_1_ne53my.mp4" type="video/mp4" />
          </video>
        )}

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/55 z-10" />

        {/* Top fade */}
        <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none"
          style={{ height: '120px', background: 'linear-gradient(to bottom, #000000, transparent)' }} />

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none"
          style={{ height: '200px', background: 'linear-gradient(to top, #000000 20%, transparent 100%)', zIndex: 30 }} />

        {/* Content */}
        <div className="relative z-20 flex items-center min-h-[80vh] px-6 md:px-16 py-20 pb-32">
          <div className="w-full grid md:grid-cols-2 gap-12 items-center">

            {/* LEFT: Text */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <span className="text-sm tracking-[0.4em] uppercase mb-4 block" style={{ color: '#0be3c5' }}>
                Hyper Edit
              </span>
              <h2 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">
                High-Energy Edits<br />for Modern Audiences
              </h2>
              <p className="text-white/70 text-lg mb-8 max-w-md">
                Fast-paced, music-driven edits designed to capture attention and dominate social media trends.
              </p>
              <div className="flex flex-row gap-4 items-center flex-wrap">
                <Link to="/work" className="btn-primary inline-flex items-center justify-center">View Work</Link>
                <a href="https://www.tiktok.com/@cayloedit?is_from_webapp=1&sender_device=pc" target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center justify-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/></svg>
                  TikTok
                </a>
              </div>
            </motion.div>

            {/* RIGHT: Floating Video Cards */}
            <motion.div
              className="flex flex-row gap-4 items-center md:items-end"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            >
              {[
                { vid: '-cMTVP5fA3c', thumb: 'https://img.youtube.com/vi/-cMTVP5fA3c/maxresdefault.jpg' },
                { vid: 'kCzNrJMPRwk', thumb: 'https://img.youtube.com/vi/kCzNrJMPRwk/maxresdefault.jpg' },
              ].map(({ vid, thumb }) => (
                <div
                  key={vid}
                  className="relative overflow-hidden rounded-2xl flex-1 cursor-pointer group"
                  style={{ boxShadow: '0 0 30px rgba(11,227,197,0.25)' }}
                  onClick={() => handleVideoClick(vid)}
                >
                  <div className="aspect-video bg-black">
                    {playingVideo === vid ? (
                      <>
                        <iframe
                          src={`https://www.youtube.com/embed/${vid}?autoplay=1&rel=0&modestbranding=1`}
                          title="App Experience"
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                        <button
                          onClick={e => { e.stopPropagation(); setPlayingVideo(null); }}
                          className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-lg backdrop-blur-sm transition-colors z-10"
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="text-white">
                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <img
                          src={thumb}
                          alt="App Experience"
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center"
                            style={{ background: 'rgba(11,227,197,0.2)', border: '2px solid #0be3c5', boxShadow: '0 0 24px rgba(11,227,197,0.4)' }}>
                            <Play size={18} className="ml-0.5" style={{ color: '#0be3c5' }} fill="#0be3c5" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>

          </div>
        </div>
      </section>



      {/* Free Preset Section */}
      <section className="py-20 bg-black relative overflow-hidden">
        {/* subtle teal glow */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(11,227,197,0.06) 0%, transparent 70%)' }} />

        <div className="relative z-10 px-6 md:px-16">
          <motion.div
            className="max-w-5xl mx-auto rounded-2xl border border-white/8 p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-10"
            style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(10px)' }}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            {/* Left */}
            <div className="flex-1 text-center md:text-left">
              <span className="text-xs tracking-[0.4em] uppercase mb-3 block" style={{ color: '#0be3c5' }}>Free Giveaway</span>
              <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-4">
                CAYLO Cinematic<br />LUT Pack
              </h2>
              <p className="text-white/50 text-base mb-6 max-w-md">
                My personal color grading preset â€” unlocks free for everyone when we hit <span className="text-white font-semibold">10K followers</span> on TikTok.
              </p>

              {/* Progress */}
              <div className="mb-6 max-w-xs mx-auto md:mx-0">
                <div className="flex justify-between text-xs text-white/40 mb-1">
                  <span>9,942 followers</span>
                  <span>10K goal</span>
                </div>
                <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.07)' }}>
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: 'linear-gradient(90deg, #0be3c5, #0891b2)' }}
                    initial={{ width: 0 }}
                    whileInView={{ width: '99.4%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.2, delay: 0.3, ease: 'easeOut' }}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                <Link
                  to="/preset"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                  Follow to Unlock
                </Link>
                <Link to="/preset" className="btn-outline inline-flex items-center justify-center">
                  See Details
                </Link>
              </div>
            </div>

            {/* Right â€” lock/unlock visual */}
            <div className="flex-shrink-0 flex flex-col items-center gap-4">
              <div
                className="w-28 h-28 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(11,227,197,0.08)', border: '2px solid rgba(11,227,197,0.3)', boxShadow: '0 0 40px rgba(11,227,197,0.15)' }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#0be3c5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <span className="text-white/30 text-xs tracking-widest uppercase">Coming Soon</span>
            </div>

          </motion.div>
        </div>
      </section>



      {/* Commercial VFX Section */}
      <section className="relative w-full bg-black overflow-hidden" style={{ minHeight: '80vh' }}>
        {!isMobile && (
          <video autoPlay muted loop playsInline preload="none" className="absolute inset-0 w-full h-full object-cover z-0" style={{ filter: 'brightness(0.85)' }}>
            <source src="https://res.cloudinary.com/ddsnj941k/video/upload/video3_umtwtw.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-black/55 z-10" />
        <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none" style={{ height: '120px', background: 'linear-gradient(to bottom, #000000, transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ height: '200px', background: 'linear-gradient(to top, #000000 20%, transparent 100%)', zIndex: 30 }} />
        <div className="relative z-20 flex items-center min-h-[80vh] px-6 md:px-16 py-20 pb-32">
          <div className="w-full grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: 'easeOut' }}>
              <span className="text-sm tracking-[0.4em] uppercase mb-4 block" style={{ color: '#0be3c5' }}>Commercial VFX</span>
              <h2 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">Visual Effects<br />That Drive Impact</h2>
              <p className="text-white/70 text-lg mb-8 max-w-md">High-performance VFX crafted to bring commercial ideas to life with cinematic precision.</p>
              <Link to="/work" className="btn-primary inline-flex items-center justify-center">View Work</Link>
            </motion.div>
            <motion.div className="flex flex-row gap-4 items-center md:items-end" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}>
              {[
                { vid: 'r8TIJZIcTAo', thumb: 'https://img.youtube.com/vi/r8TIJZIcTAo/maxresdefault.jpg' },
                { vid: 'j5PLh659OH4', thumb: 'https://img.youtube.com/vi/j5PLh659OH4/maxresdefault.jpg' },
              ].map(({ vid, thumb }) => (
                <div key={vid} className="relative overflow-hidden rounded-2xl flex-1 cursor-pointer group" style={{ boxShadow: '0 0 30px rgba(11,227,197,0.25)' }} onClick={() => handleVideoClick(vid)}>
                  <div className="aspect-video bg-black">
                    {playingVideo === vid ? (
                      <><iframe src={`https://www.youtube.com/embed/${vid}?autoplay=1&rel=0&modestbranding=1`} title="VFX" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        <button onClick={e => { e.stopPropagation(); setPlayingVideo(null); }} className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-lg backdrop-blur-sm transition-colors z-10"><svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="text-white"><path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></button></>
                    ) : (
                      <><img src={thumb} alt="VFX" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center"><div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(11,227,197,0.2)', border: '2px solid #0be3c5', boxShadow: '0 0 24px rgba(11,227,197,0.4)' }}><Play size={18} className="ml-0.5" style={{ color: '#0be3c5' }} fill="#0be3c5" /></div></div></>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>



      {/* YouTube Editing Preview Section */}
      <section className="pt-10 pb-10 bg-black">
        <div className="px-6 md:px-10">
          <motion.div className="flex items-end justify-between mb-2 md:mb-4" initial={{ opacity: 0, y: 98 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, ease: 'easeInOut' }}>
            <div>
              <span className="text-white/40 text-sm tracking-[0.3em] uppercase">YouTube</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-2">YouTube Editing</h2>
            </div>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { id: 'LYdD8cmVi8M', title: 'Mobile Animation & VFX â€” KTM Commercial' },
              { id: '8OlMw3zEODg', title: '3D App UI Animation' },
              { id: 'NzaWHgXC9mM', title: 'KTM Commercial' },
              { id: 'dQw4w9WgXcQ', title: 'YouTube Edit Reel' },
            ].map((video, index) => (
              <motion.div key={video.id} className="group relative overflow-hidden bg-black cursor-pointer" initial={{ opacity: 0, y: 98 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 1, delay: index * 0.3, ease: 'easeInOut' }} onClick={() => handleVideoClick(video.id)}>
                {playingVideo === video.id ? (
                  <div className="relative aspect-video">
                    <iframe src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`} title={video.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    <button onClick={e => { e.stopPropagation(); setPlayingVideo(null); }} className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-lg backdrop-blur-sm transition-colors z-10"><svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="text-white"><path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></button>
                  </div>
                ) : (
                  <div className="relative aspect-video">
                    <img src={`https://img.youtube.com/vi/${video.id}/maxresdefault.jpg`} alt={video.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"><div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(11,227,197,0.2)', border: '2px solid #0be3c5', boxShadow: '0 0 24px rgba(11,227,197,0.4)' }}><Play size={18} className="ml-0.5" style={{ color: '#0be3c5' }} fill="#0be3c5" /></div></div>
                    <div className="absolute bottom-0 left-0 right-0 p-3"><h3 className="text-white font-bold uppercase text-xs md:text-sm leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>{video.title}</h3></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/youtube-edits" className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-lg">
              View More YouTube Projects <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>



      {/* Motion Design / Cinematic App Section */}
      <section className="relative w-full bg-black overflow-hidden" style={{ minHeight: '80vh' }}>
        {!isMobile && (
          <video autoPlay muted loop playsInline preload="none" className="absolute inset-0 w-full h-full object-cover z-0" style={{ filter: 'brightness(0.85)' }}>
            <source src="https://res.cloudinary.com/ddsnj941k/video/upload/video4_d2td9f.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-black/55 z-10" />
        <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none" style={{ height: '120px', background: 'linear-gradient(to bottom, #000000, transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ height: '200px', background: 'linear-gradient(to top, #000000 20%, transparent 100%)', zIndex: 30 }} />
        <div className="relative z-20 flex items-center min-h-[80vh] px-6 md:px-16 py-20 pb-32">
          <div className="w-full grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: 'easeOut' }}>
              <span className="text-sm tracking-[0.4em] uppercase mb-4 block" style={{ color: '#0be3c5' }}>Motion Design</span>
              <h2 className="text-4xl md:text-6xl font-black text-white leading-tight mb-6">Cinematic App<br />Experiences</h2>
              <p className="text-white/70 text-lg mb-8 max-w-md">Crafting immersive UI animations that transform simple interfaces into engaging visual stories.</p>
              <Link to="/work" className="btn-primary inline-flex items-center justify-center">View Work</Link>
            </motion.div>
            <motion.div className="flex flex-row gap-4 items-center md:items-end" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}>
              {[
                { vid: '8OlMw3zEODg', thumb: 'https://img.youtube.com/vi/8OlMw3zEODg/maxresdefault.jpg' },
                { vid: '5A_1AoiVNNs', thumb: 'https://img.youtube.com/vi/5A_1AoiVNNs/mqdefault.jpg' },
              ].map(({ vid, thumb }) => (
                <div key={vid} className="relative overflow-hidden rounded-2xl flex-1 cursor-pointer group" style={{ boxShadow: '0 0 30px rgba(11,227,197,0.25)' }} onClick={() => handleVideoClick(vid)}>
                  <div className="aspect-video bg-black">
                    {playingVideo === vid ? (
                      <><iframe src={`https://www.youtube.com/embed/${vid}?autoplay=1&rel=0&modestbranding=1`} title="App Experience" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        <button onClick={e => { e.stopPropagation(); setPlayingVideo(null); }} className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 rounded-lg backdrop-blur-sm transition-colors z-10"><svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="text-white"><path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></button></>
                    ) : (
                      <><img src={thumb} alt="App Experience" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center"><div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(11,227,197,0.2)', border: '2px solid #0be3c5', boxShadow: '0 0 24px rgba(11,227,197,0.4)' }}><Play size={18} className="ml-0.5" style={{ color: '#0be3c5' }} fill="#0be3c5" /></div></div></>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
      {/* Client Reviews Preview */}
      <section className="section-padding bg-black">
        <div className="container-custom">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-white/40 text-sm tracking-[0.3em] uppercase">Testimonials</span>
            <h2 className="text-4xl md:text-5xl font-bold mt-2">What Clients Say</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {clientReviews.map((review, index) => (
              <motion.div
                key={review.id}
                className="glass-card-hover p-6 rounded-xl"
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <p className="text-white/70 mb-4">&ldquo;{review.feedback}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                    <span className="text-white font-medium">{review.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{review.name}</p>
                    <p className="text-white/40 text-sm">{review.projectType}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/feedback"
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-lg"
            >
              See More Reviews <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>



      {/* Contact CTA Section */}
      <section className="section-padding bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent" />
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Create?</h2>
            <p className="text-white/60 text-lg mb-8">
              Let&apos;s bring your vision to life. Whether it&apos;s a brand film, documentary, 
              or creative projectâ€”I&apos;m here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                Start a Project <ArrowRight size={18} />
              </Link>
              <Link to="/work" className="btn-outline inline-flex items-center justify-center gap-2">
                View Portfolio
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}


